import React from 'react';
import { useTranslation } from 'react-i18next';
import { editorStore } from '../../../../store/editorStore';
import { SingleLineType } from '../../../../types/floorplannerTypes';
import { getArcBulge, getDefaultControlPoint } from '../../../../utils/arcUtils';
import { panelObjectTitleRowClassName, toDisplayLength, toWorldFromDisplay } from '../propertyPanelUtils';
import DimensionField from '../components/DimensionField';
import MetricsSection from '../components/MetricsSection';
import UnitHeader from '../components/UnitHeader';
import type { PropertyPanelController } from '../types';

type SingleLineSectionProps = Pick<PropertyPanelController, 'singleLineLength' | 'setSingleLineLength' | 'updateGlobalState'> & {
  selectedSingleLines: SingleLineType[];
  displayedSingleLines: SingleLineType[];
  commonSingleLineLength?: number;
};

const SingleLineSection: React.FC<SingleLineSectionProps> = ({
  selectedSingleLines,
  displayedSingleLines,
  commonSingleLineLength,
  singleLineLength,
  setSingleLineLength,
  updateGlobalState,
}) => {
  const { t } = useTranslation();
  const mixed = t('common.mixed');

  return (
    <>
      {displayedSingleLines.map((line) => (
        <div key={`singleLine-${line?.id}`}>
          <div className={panelObjectTitleRowClassName}>
            <UnitHeader title={selectedSingleLines.length > 1 ? t('propertyPanel.multipleSingleLines', { count: selectedSingleLines.length }) : t('shapes.singleLine')} />
          </div>
          <MetricsSection>
            <div className="flex items-center justify-between py-2">
              <DimensionField
                id="singleLineLength"
                label={t('propertyPanel.length')}
                value={selectedSingleLines.length > 1 ? (commonSingleLineLength ?? '') : singleLineLength}
                placeholder={selectedSingleLines.length > 1 ? mixed : undefined}
                onChange={(nextValue) => {
                  let value: number | '';
                  if (isNaN(parseFloat(nextValue)) || parseFloat(nextValue) < 0) {
                    value = '';
                  } else {
                    value = Math.max(1, nextValue ? parseFloat(nextValue) : 1);
                  }
                  setSingleLineLength(value);
                  updateGlobalState(line?.id, 'length', value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    updateGlobalState(line?.id, 'length', singleLineLength);
                    editorStore.setSingleLineEditingLengthPanel(undefined);
                  }
                }}
              />
            </div>
            {selectedSingleLines.length === 1 && (
              <div className="flex flex-row items-center justify-start py-2">
                <DimensionField
                  label={t('propertyPanel.bulge')}
                  value={Math.round(toDisplayLength(line?.controlPoint ? getArcBulge(line) : 0))}
                  onChange={(nextValue) => {
                    const bulgeInMm = parseFloat(nextValue) || 0;
                    const bulgeInWorld = toWorldFromDisplay(bulgeInMm);
                    if (Math.abs(bulgeInWorld) < 1e-6) {
                      updateGlobalState(line?.id, 'lineForm', 'straight');
                      updateGlobalState(line?.id, 'controlPoint', undefined);
                    } else {
                      const cp = getDefaultControlPoint(line.start, line.end, bulgeInWorld);
                      updateGlobalState(line?.id, 'lineForm', 'arc');
                      updateGlobalState(line?.id, 'controlPoint', cp);
                    }
                  }}
                />
              </div>
            )}
          </MetricsSection>
          <div className="border-b border-gray-300" />
        </div>
      ))}
    </>
  );
};

export default SingleLineSection;
